import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "antd";

const CalibrationRowSpacingStep = ({
    onNext,
    onBack,
    onClose,
    imgSrc,
    viewState,
    placedSeats = [],
    seatSize = 18,
    onRowSpacingChange,
}) => {
    const previewRef = useRef(null);
    const dragStartRef = useRef({
        startX: 0,
        startY: 0,
        originOffsetX: 0,
        originOffsetY: 0,
    });

    const [dragging, setDragging] = useState(false);
    const [rowOffset, setRowOffset] = useState({ x: 0, y: seatSize * 3 });

    const { zoom, offset } = viewState || {
        zoom: 300,
        offset: { x: 0, y: 0 },
    };

    const scale = (viewState?.zoom || 100) / 100;

    const seatSizeScaled = seatSize * scale;

    const baseRowSeats = useMemo(() => {
        if (!Array.isArray(placedSeats) || !placedSeats.length) return [];

        const sorted = [...placedSeats].sort((a, b) => a.x - b.x);
        return sorted.slice(0, 4);
    }, [placedSeats]);

    const highlightedSeats = useMemo(() => {
        return baseRowSeats.map((seat) => ({
            ...seat,
            x: seat.x + rowOffset.x,
            y: seat.y + rowOffset.y,
        }));
    }, [baseRowSeats, rowOffset]);

    const imageLayerStyle = useMemo(() => {
        return {
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom / 100})`,
            transformOrigin: "top left",
        };
    }, [offset, zoom]);

    useEffect(() => {
        if (onRowSpacingChange) {
            onRowSpacingChange(rowOffset);
        }
    }, [rowOffset, onRowSpacingChange]);

    useEffect(() => {
        const handleWindowMouseMove = (event) => {
            if (!dragging) return;

            const deltaX = event.clientX - dragStartRef.current.startX;
            const deltaY = event.clientY - dragStartRef.current.startY;

            setRowOffset({
                x: dragStartRef.current.originOffsetX + deltaX,
                y: dragStartRef.current.originOffsetY + deltaY,
            });
        };

        const handleWindowMouseUp = () => {
            setDragging(false);
        };

        window.addEventListener("mousemove", handleWindowMouseMove);
        window.addEventListener("mouseup", handleWindowMouseUp);

        return () => {
            window.removeEventListener("mousemove", handleWindowMouseMove);
            window.removeEventListener("mouseup", handleWindowMouseUp);
        };
    }, [dragging]);

    const handleHighlightedRowMouseDown = (event) => {
        event.preventDefault();
        event.stopPropagation();

        dragStartRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            originOffsetX: rowOffset.x,
            originOffsetY: rowOffset.y,
        };

        setDragging(true);
    };

    return (
        <div className="calibration-row-spacing-step">
            <button
                type="button"
                className="calibration-row-spacing-step__close"
                onClick={onClose}
            >
                ✕
            </button>

            <div className="calibration-row-spacing-step__wrapper">
                <div className="calibration-row-spacing-step__header">
                    <div className="calibration-row-spacing-step__badge">4</div>

                    <div className="calibration-row-spacing-step__title-group">
                        <h1>
                            Define el espaciado entre filas
                        </h1>
                        <p>
                            Arrastra la fila hasta alinearla con la siguiente y confirma con "Finalizar".
                        </p>
                    </div>
                </div>

                <div className="calibration-row-spacing-step__preview-card">
                    <div
                        ref={previewRef}
                        className={`calibration-row-spacing-step__preview ${dragging ? "is-dragging" : ""}`}
                    >
                        {imgSrc && (
                            <div
                                className="calibration-row-spacing-step__image-layer"
                                style={imageLayerStyle}
                            >
                                <img
                                    src={imgSrc}
                                    alt="Reference chart"
                                    draggable={false}
                                />
                            </div>
                        )}

                        {baseRowSeats.map((seat) => (
                            <div
                                key={`base-${seat.id}`}
                                className="calibration-row-spacing-step__seat calibration-row-spacing-step__seat--base"
                                style={{
                                    width: `${seatSizeScaled}px`,
                                    height: `${seatSizeScaled}px`,
                                    left: `${seat.x}px`,
                                    top: `${seat.y}px`,
                                    transform: "translate(-50%, -50%)",
                                }}
                            />
                        ))}

                        <div
                            className="calibration-row-spacing-step__highlighted-row"
                            onMouseDown={handleHighlightedRowMouseDown}
                        >
                            {highlightedSeats.map((seat) => (
                                <div
                                    key={`highlighted-${seat.id}`}
                                    className="calibration-row-spacing-step__seat calibration-row-spacing-step__seat--highlighted"
                                    style={{
                                        width: `${seatSizeScaled}px`,
                                        height: `${seatSizeScaled}px`,
                                        left: `${seat.x}px`,
                                        top: `${seat.y}px`,
                                        transform: "translate(-50%, -50%)",
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <div className="calibration-row-spacing-step__actions">
                    <Button type="link" onClick={onBack}>
                        Atras
                    </Button>

                    <Button type="link" onClick={onNext}>
                        Finalizar
                    </Button>
                </div>
            </div>
        </div>
    );
};

export { CalibrationRowSpacingStep };